import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import './VisibilityTrends.css';

interface VisibilityTrendsProps {
  onNavigateToScan?: () => void;
}

export default function VisibilityTrends({ onNavigateToScan }: VisibilityTrendsProps) {
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Always pass function references; use undefined args to skip conditionally
  const allTrends = useQuery(api.visibilityTrends.getAllVisibilityTrends, { limit: 100 });
  const contentTrends = useQuery(
    api.visibilityTrends.getVisibilityTrendsForContent,
    selectedTitle ? { contentTitle: selectedTitle } : undefined
  );

  if (!allTrends) {
    return (
      <div className="visibility-trends">
        <div className="loading">Loading visibility trends...</div>
      </div>
    );
  }

  // Empty state - no scans have been run yet
  if (allTrends.trends.length === 0) {
    return (
      <div className="visibility-trends">
        <div className="empty-state-page">
          <div className="empty-icon">📈</div>
          <h2>No Visibility Data Yet</h2>
          <p className="empty-description">
            Search visibility tracking shows how well OSN+ content ranks in search results compared to piracy sites.
          </p>
          <p className="empty-hint">
            Run your first scan to start tracking visibility trends over time.
          </p>
          {onNavigateToScan && (
            <button className="btn-primary" onClick={onNavigateToScan}>
              🔍 Run Your First Scan
            </button>
          )}
        </div>
      </div>
    );
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return '📈';
      case 'declining':
        return '📉';
      default:
        return '➡️';
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'improving':
        return 'trend-improving';
      case 'declining':
        return 'trend-declining';
      default:
        return 'trend-stable';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    if (score >= 40) return 'score-fair';
    return 'score-poor';
  };

  return (
    <div className="visibility-trends">

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon">📚</div>
          <div className="card-content">
            <div className="card-value">{allTrends.totalTitles}</div>
            <div className="card-label">Content Titles Tracked</div>
          </div>
        </div>

        <div className="summary-card trend-improving">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <div className="card-value">{allTrends.summary.improving}</div>
            <div className="card-label">Improving</div>
          </div>
        </div>

        <div className="summary-card trend-stable">
          <div className="card-icon">➡️</div>
          <div className="card-content">
            <div className="card-value">{allTrends.summary.stable}</div>
            <div className="card-label">Stable</div>
          </div>
        </div>

        <div className="summary-card trend-declining">
          <div className="card-icon">📉</div>
          <div className="card-content">
            <div className="card-value">{allTrends.summary.declining}</div>
            <div className="card-label">Declining</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon">⭐</div>
          <div className="card-content">
            <div className="card-value">{allTrends.summary.avgScore}</div>
            <div className="card-label">Average Score</div>
          </div>
        </div>
      </div>

      {/* Trends Table */}
      <div className="trends-section">
        <div className="trends-section-header">
          <h3>Content Visibility Performance</h3>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search titles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        <div className="trends-table">
          <div className="table-header">
            <div className="col-title">Content Title</div>
            <div className="col-scans">Scans</div>
            <div className="col-latest">Latest Score</div>
            <div className="col-avg">Avg Score</div>
            <div className="col-violations">Violations</div>
            <div className="col-change">Recent Change</div>
            <div className="col-trend">Trend</div>
            <div className="col-date">Last Scanned</div>
          </div>

          <div className="table-body">
            {allTrends.trends
              .filter((trend) =>
                trend.contentTitle.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .sort((a, b) => a.contentTitle.localeCompare(b.contentTitle))
              .map((trend) => (
              <div
                key={trend.contentTitle}
                className={`trend-row ${getTrendColor(trend.trend)}`}
                onClick={() => setSelectedTitle(trend.contentTitle)}
              >
                <div className="col-title">
                  <span className="title-name">{trend.contentTitle}</span>
                </div>

                <div className="col-scans">
                  <span className="scan-count">{trend.scanCount}</span>
                </div>

                <div className="col-latest">
                  <span className={`score-badge ${getScoreColor(trend.latestScore)}`}>
                    {trend.latestScore}
                  </span>
                </div>

                <div className="col-avg">
                  <span className="avg-score">{trend.avgScore}</span>
                </div>

                <div className="col-violations">
                  <span className="violation-count">{trend.violationCount}</span>
                </div>

                <div className="col-change">
                  <span className={`change-value ${trend.recentChange >= 0 ? 'positive' : 'negative'}`}>
                    {trend.recentChange >= 0 ? '+' : ''}
                    {trend.recentChange}
                  </span>
                </div>

                <div className="col-trend">
                  <span className={`trend-badge ${getTrendColor(trend.trend)}`}>
                    {getTrendIcon(trend.trend)} {trend.trend}
                  </span>
                </div>

                <div className="col-date">
                  <span className="date">
                    {new Date(trend.latestScanDate || 0).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}

            {allTrends.trends.filter((trend) =>
                trend.contentTitle.toLowerCase().includes(searchQuery.toLowerCase())
              ).length === 0 && (
              <div className="empty-state">
                {searchQuery ? (
                  <>
                    <p>No titles match "{searchQuery}"</p>
                    <p>Try a different search term.</p>
                  </>
                ) : (
                  <>
                    <p>No visibility trends available yet.</p>
                    <p>Visibility scores are calculated after each scan completes.</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Detail Modal */}
      {selectedTitle && (
        <div className="modal-overlay" onClick={() => setSelectedTitle(null)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-modern">
              <h3>{selectedTitle}</h3>
              <button className="close-btn" onClick={() => setSelectedTitle(null)}>
                ×
              </button>
            </div>

            {contentTrends && (
            <div className="modal-body-modern">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                <div className="stat-card">
                  <div className="stat-label">TOTAL SCANS:</div>
                  <div className="stat-value-large">{contentTrends.summary.totalScans}</div>
                </div>

                <div className={`stat-card stat-${getScoreColor(contentTrends.summary.averageScore)}`}>
                  <div className="stat-label">AVERAGE SCORE:</div>
                  <div className="stat-value-large">{contentTrends.summary.averageScore}</div>
                </div>

                <div className={`stat-card stat-${getScoreColor(contentTrends.summary.bestScore)}`}>
                  <div className="stat-label">BEST SCORE:</div>
                  <div className="stat-value-large">{contentTrends.summary.bestScore}</div>
                </div>

                <div className={`stat-card stat-${getScoreColor(contentTrends.summary.worstScore)}`}>
                  <div className="stat-label">WORST SCORE:</div>
                  <div className="stat-value-large">{contentTrends.summary.worstScore}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                <div className={`stat-card stat-trend-${contentTrends.summary.trend}`}>
                  <div className="stat-label">OVERALL TREND:</div>
                  <div className="flex items-center gap-2">
                    <span className="stat-trend-icon">{getTrendIcon(contentTrends.summary.trend)}</span>
                    <span className="stat-trend-text">{contentTrends.summary.trend}</span>
                  </div>
                </div>

                <div className={`stat-card ${contentTrends.summary.improvement >= 0 ? 'stat-positive' : 'stat-negative'}`}>
                  <div className="stat-label">IMPROVEMENT:</div>
                  <div className="stat-value-large">
                    {contentTrends.summary.improvement >= 0 ? '+' : ''}
                    {contentTrends.summary.improvement} points
                  </div>
                </div>
              </div>

              {/* Charts */}
              {contentTrends.dataPoints.length > 0 && (
                <div className="charts-grid">
                  {/* Visibility Score Chart */}
                  <div className="chart-section-modern">
                    <h4 className="chart-title">Visibility Score Over Time</h4>
                    <div className="simple-chart">
                      <div className="chart-y-axis">
                        <span>100</span>
                        <span>75</span>
                        <span>50</span>
                        <span>25</span>
                        <span>0</span>
                      </div>
                      <div className="chart-container">
                        <div className="chart-grid">
                          {[0, 25, 50, 75, 100].map((line) => (
                            <div
                              key={line}
                              className="grid-line"
                              style={{ bottom: `${line}%` }}
                            />
                          ))}
                        </div>
                        <svg className="chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                          <polyline
                            points={contentTrends.dataPoints
                              .map((point, idx) => {
                                const x = (idx / (contentTrends.dataPoints.length - 1)) * 100;
                                const y = 100 - point.visibilityScore;
                                return `${x},${y}`;
                              })
                              .join(' ')}
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="2"
                          />
                          {contentTrends.dataPoints.map((point, idx) => {
                            const x = (idx / (contentTrends.dataPoints.length - 1)) * 100;
                            const y = 100 - point.visibilityScore;
                            return (
                              <circle
                                key={idx}
                                cx={x}
                                cy={y}
                                r="1.5"
                                fill="#3b82f6"
                              />
                            );
                          })}
                        </svg>
                      </div>
                    </div>
                    <div className="chart-x-labels">
                      <span>{new Date(contentTrends.dataPoints[0].date).toLocaleDateString()}</span>
                      <span>
                        {new Date(
                          contentTrends.dataPoints[contentTrends.dataPoints.length - 1].date
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Pirate Sites Chart */}
                  <div className="chart-section-modern">
                    <h4 className="chart-title">Pirate Sites Detected Over Time</h4>
                    <div className="simple-chart">
                      <div className="chart-y-axis">
                        {(() => {
                          const maxViolations = Math.max(...contentTrends.dataPoints.map(p => p.violationCount));
                          const step = Math.ceil(maxViolations / 4);
                          return [maxViolations, step * 3, step * 2, step, 0].map((val, idx) => (
                            <span key={idx}>{val}</span>
                          ));
                        })()}
                      </div>
                      <div className="chart-container">
                        <div className="chart-grid">
                          {[0, 25, 50, 75, 100].map((line) => (
                            <div
                              key={line}
                              className="grid-line"
                              style={{ bottom: `${line}%` }}
                            />
                          ))}
                        </div>
                        <svg className="chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                          {(() => {
                            const maxViolations = Math.max(...contentTrends.dataPoints.map(p => p.violationCount));
                            return (
                              <>
                                <polyline
                                  points={contentTrends.dataPoints
                                    .map((point, idx) => {
                                      const x = (idx / (contentTrends.dataPoints.length - 1)) * 100;
                                      const y = 100 - (point.violationCount / maxViolations * 100);
                                      return `${x},${y}`;
                                    })
                                    .join(' ')}
                                  fill="none"
                                  stroke="#ef4444"
                                  strokeWidth="2"
                                />
                                {contentTrends.dataPoints.map((point, idx) => {
                                  const x = (idx / (contentTrends.dataPoints.length - 1)) * 100;
                                  const y = 100 - (point.violationCount / maxViolations * 100);
                                  return (
                                    <circle
                                      key={idx}
                                      cx={x}
                                      cy={y}
                                      r="1.5"
                                      fill="#ef4444"
                                    />
                                  );
                                })}
                              </>
                            );
                          })()}
                        </svg>
                      </div>
                    </div>
                    <div className="chart-x-labels">
                      <span>{new Date(contentTrends.dataPoints[0].date).toLocaleDateString()}</span>
                      <span>
                        {new Date(
                          contentTrends.dataPoints[contentTrends.dataPoints.length - 1].date
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Data Points List */}
              <div className="datapoints-section">
                <h4>Scan History ({contentTrends.dataPoints.length})</h4>
                <div className="datapoints-list">
                  {contentTrends.dataPoints
                    .slice()
                    .reverse()
                    .map((point, idx) => (
                      <div key={idx} className="datapoint-item">
                        <div className="datapoint-date">
                          {new Date(point.date).toLocaleString()}
                        </div>
                        <div className="datapoint-metrics">
                          <span className={`score-badge ${getScoreColor(point.visibilityScore)}`}>
                            Score: {point.visibilityScore}
                          </span>
                          {point.osnRank && (
                            <span className="metric-badge">OSN Rank: #{point.osnRank}</span>
                          )}
                          {point.piratesBeforeOsn !== undefined && (
                            <span className="metric-badge warn">
                              Pirates Before OSN: {point.piratesBeforeOsn}
                            </span>
                          )}
                          <span className="metric-badge">
                            Violations: {point.violationCount}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
